import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Alert,
} from 'reactstrap';
import { roles as fetchRoles, roleCreate, roleUpdate, roleDelete, fetchPermissionRegistry } from '../../redux/actions/user';
import { userList } from '../../redux/actions/user';
import Loading from '../../components/Loading/Loading';
import PermissionMatrix from './PermissionMatrix';
import { hasPermission } from '../../utils/permissions';

const RolesTab = () => {
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const rolesList = userState?.roles || [];
  const users = userState?.data || [];
  const roleAction = userState?.roleAction;
  const permissionRegistry = userState?.permissionRegistry || {};
  const permissionRegistryError = userState?.permissionRegistryError || null;

  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formName, setFormName] = useState('');
  const [formPrivilege, setFormPrivilege] = useState(0);
  const [formPermissions, setFormPermissions] = useState({});
  const [alert, setAlert] = useState(null);
  const alertTimeoutRef = useRef(null);

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(userList());
    dispatch(fetchPermissionRegistry());
  }, [dispatch]);

  useEffect(() => {
    if (rolesList.length > 0 || userState?.roles) {
      setLoading(false);
    }
  }, [rolesList, userState?.roles]);

  useEffect(() => {
    if (roleAction) {
      setAlert({ color: roleAction.status === 'success' ? 'success' : 'danger', message: roleAction.message });
      if (roleAction.status === 'success') {
        setModal(false);
        setDeleteModal(false);
        dispatch(fetchRoles());
        dispatch(userList());
      }
      if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
      alertTimeoutRef.current = setTimeout(() => setAlert(null), 4000);
    }
  }, [roleAction, dispatch]);

  const getUserCount = (roleName) => {
    return users.filter((u) => u.role_name === roleName).length;
  };

  const getPrivilegeBadgeColor = (level) => {
    if (level === 0) return 'danger';
    if (level === 1) return 'primary';
    if (level === 2) return 'warning';
    return 'secondary';
  };

  const getPermissionCount = (role) => {
    const perms = role.permissions || {};
    return Object.values(perms).filter((v) => v === true).length;
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setFormName('');
    setFormPrivilege(0);
    setFormPermissions({});
    setModal(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setFormName(role.role_name);
    setFormPrivilege(role.privilege_level);
    setFormPermissions(role.permissions || {});
    setModal(true);
  };

  const openDeleteModal = (role) => {
    setEditingRole(role);
    setDeleteModal(true);
  };

  const handleSubmit = () => {
    if (!formName.trim()) {
      setAlert({ color: 'danger', message: 'El nombre del rol es obligatorio.' });
      return;
    }
    if (editingRole) {
      dispatch(roleUpdate(editingRole.id, formName, formPrivilege, formPermissions));
    } else {
      dispatch(roleCreate(formName, formPrivilege, formPermissions));
    }
  };

  const handleDelete = () => {
    if (editingRole) {
      dispatch(roleDelete(editingRole.id));
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      {alert && (
        <Alert color={alert.color} toggle={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Roles del sistema</h5>
        {hasPermission('roles.create') && (
          <Button color="success" size="sm" onClick={openCreateModal}>
            + Crear Rol
          </Button>
        )}
      </div>

      <Table responsive hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Nombre</th>
            <th>Nivel de Privilegio</th>
            <th>Permisos</th>
            <th>Usuarios asignados</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rolesList.map((role, index) => (
            <tr key={index}>
              <td>{role.id}</td>
              <td>
                <Badge color={getPrivilegeBadgeColor(role.privilege_level)}>
                  {role.role_name}
                </Badge>
              </td>
              <td>{role.privilege_level}</td>
              <td>
                <Badge color="info">{getPermissionCount(role)} permisos</Badge>
              </td>
              <td>{getUserCount(role.role_name)}</td>
              <td>
                {hasPermission('roles.edit') && (
                  <Button
                    color="primary"
                    size="sm"
                    className="mr-2"
                    onClick={() => openEditModal(role)}
                  >
                    Editar
                  </Button>
                )}{' '}
                {hasPermission('roles.delete') && (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => openDeleteModal(role)}
                  >
                    Eliminar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Create/Edit Modal */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          {editingRole ? 'Editar Rol' : 'Crear Rol'}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Nombre del rol</Label>
            <Input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ej: supervisor"
            />
          </FormGroup>
          <FormGroup>
            <Label>Nivel de Privilegio</Label>
            <Input
              type="number"
              min="0"
              value={formPrivilege}
              onChange={(e) => setFormPrivilege(parseInt(e.target.value, 10) || 0)}
            />
            <small className="text-muted">
              0 = mayor privilegio (admin), mayor numero = menor privilegio
            </small>
          </FormGroup>
          <FormGroup>
            <Label>Permisos</Label>
            <PermissionMatrix
              registry={permissionRegistry}
              registryError={permissionRegistryError}
              permissions={formPermissions}
              onChange={setFormPermissions}
              onRetry={() => dispatch(fetchPermissionRegistry())}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModal(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            {editingRole ? 'Actualizar' : 'Crear'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Eliminar Rol
        </ModalHeader>
        <ModalBody>
          {editingRole && (
            <p>
              ¿Está seguro que desea eliminar el rol{' '}
              <strong>{editingRole.role_name}</strong>?
              {getUserCount(editingRole.role_name) > 0 && (
                <span className="text-danger d-block mt-2">
                  Este rol tiene {getUserCount(editingRole.role_name)} usuario(s)
                  asignado(s). No se podrá eliminar.
                </span>
              )}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancelar
          </Button>
          <Button
            color="danger"
            onClick={handleDelete}
            disabled={editingRole && getUserCount(editingRole.role_name) > 0}
          >
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default RolesTab;
